import type { ApplicationConfig } from '@angular/core';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { ErrorHandler, inject, provideAppInitializer } from '@angular/core';
import { provideClientHydration, withEventReplay, withHttpTransferCacheOptions, withIncrementalHydration } from '@angular/platform-browser';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling, withRouterConfig } from '@angular/router';
import { BugsnagErrorHandler } from '@bugsnag/plugin-angular';
import { ConfirmationService } from 'primeng/api';

import { LoggerService, provideCdnDeviceType, provideWindow } from '@accesso/cc-std-lib';
import { registerDayjsUtc } from '@accesso/cc-ui';
import { API_CONFIGURATION, provideJsonApi } from '@accesso/ng-json-api';
import { provideAccessoTheme } from '@accesso/ng-uds/theme';

import { routes } from './app.routes';
import { eventMapperInitializer, provideEventMapperBrowserService } from './core/analytics/event-mapper.browser.service';
import { provideOidcConfig } from './core/auth/oidc.provider';
import { provideBrowserEnvironmentService } from './core/environment/environment.browser.service';
import { provideCurrency } from './core/initializer/currency.initializer';
import { dataInitializer } from './core/initializer/data.initializer';
import { provideDatePipeOptions } from './core/initializer/date-pipe-options.initializer';
import { jsonApiHydrationInitializer } from './core/initializer/json-api-hydration.initializer';
import { localizationDataInitializer, provideLocaleId } from './core/initializer/localization.initializer';
import { provideNgOptimizedImageInitializer } from './core/initializer/ng-optimized-image.initializer';
import { anonTokenInterceptor } from './core/interceptors/anon-token.interceptor';
import { authTokenInterceptor } from './core/interceptors/auth-token.interceptor';
import { basketInterceptor } from './core/interceptors/basket.interceptor';
import { providePageSetupBrowserService } from './core/page-setup/page-setup.browser.service';
import { BUGSNAG_API_KEY, BugsnagClient } from './feature/bugsnag/bugsnag.client';
import { provideBugsnagBrowserClient } from './feature/bugsnag/bugsnag.provider.browser';
import { PageViewTrackerService } from './feature/routing/page-view-tracker.service';
import { providePreserveQueryParametersStrategy } from './feature/routing/presist-query-parameters-strategy';
import { provideLocalStorageService } from './feature/storage/local-storage.service';
import { provideThirdPartyBrowserService } from './feature/third-party/third-party.browser.service';
import { provideBrowserSessionId } from './feature/user/session-id/session-id.browser';

registerDayjsUtc();

export const appConfig: ApplicationConfig = {
	providers: [
		LoggerService,
		ConfirmationService,
		provideWindow(),
		{
			provide: API_CONFIGURATION,
			useValue: { host: 'localhost' },
		},
		{
			provide: BUGSNAG_API_KEY,
			useValue: 'd77412d028eb86f25301b8a0b904dd50',
		},
		{
			provide: ErrorHandler,
			useFactory: () => new BugsnagErrorHandler(),
		},
		provideBugsnagBrowserClient(),
		provideAccessoTheme(),
		provideCurrency(),
		provideDatePipeOptions(),
		providePageSetupBrowserService(),
		provideThirdPartyBrowserService(),
		provideEventMapperBrowserService(),
		provideBrowserEnvironmentService(),
		providePreserveQueryParametersStrategy(),
		provideLocalStorageService(),
		provideHttpClient(withFetch(), withInterceptors([authTokenInterceptor, basketInterceptor, anonTokenInterceptor])),
		provideRouter(
			routes,
			withInMemoryScrolling({ scrollPositionRestoration: 'top' }),
			withComponentInputBinding(),
			withRouterConfig({ paramsInheritanceStrategy: 'always' })
		),
		provideJsonApi(),
		provideClientHydration(
			withEventReplay(),
			withIncrementalHydration(),
			withHttpTransferCacheOptions({
				// We do not want to include the theme request in the transfer state as it wont ever be used again
				filter: (req) => !req.url.includes('/v1/theme/'),
			})
		),
		provideNgOptimizedImageInitializer(),
		provideBrowserSessionId(),
		provideLocaleId(),
		provideCdnDeviceType(),
		provideOidcConfig(),
		provideAppInitializer(jsonApiHydrationInitializer),
		provideAppInitializer(dataInitializer),
		provideAppInitializer(eventMapperInitializer),
		provideAppInitializer(localizationDataInitializer),
		provideAppInitializer(() => {
			// We just want to create it the following
			inject(PageViewTrackerService);
			inject(BugsnagClient);
		}),
	],
};
